import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import SelectDropdown from '../../customStyles/SelectDropdown';

const DesignationForm = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  isEditMode,
  isViewMode,
  initialData,
  departments = [],
  divisions = []
}) => {
  const [formData, setFormData] = useState({
    title: '',
    level: 1,
    description: '',
    departmentId: '',
    divisionId: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && initialData) {
      setFormData({
        title: initialData.title || '',
        level: initialData.level || 1,
        description: initialData.description || '',
        departmentId: initialData.departmentId?._id || initialData.departmentId || '',
        divisionId: initialData.divisionId?._id || initialData.divisionId || ''
      });
    } else if (isOpen && !initialData) {
      setFormData({ title: '', level: 1, description: '', departmentId: '', divisionId: '' });
    }
  }, [isOpen, initialData]);

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Designation title is required';
    if (!formData.level || formData.level < 1) errors.level = 'Valid level is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || isViewMode) return;

    setLoading(true);
    try {
      let response;
      if (isEditMode && initialData?._id) {
    response = await api.patch(SummaryApi.updateDesignation.url(initialData._id), formData);
      } else {
        response = await api.post(SummaryApi.createDesignation.url, formData);
      }

      if (response.data.success) {
        toast.success(`Designation ${isEditMode ? 'updated' : 'created'} successfully`);
        onSuccess();
        handleClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormErrors({});
    onClose();
  };

  return (
    <>
      <div className={`fixed inset-0 top-[57px] md:top-[65px] bg-black transition-opacity duration-300 z-[65] ${isOpen ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'}`} onClick={handleClose} />
      <div className={`fixed top-[57px] md:top-[65px] right-0 h-full w-full max-w-2xl bg-white shadow-2xl z-[65] transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="h-full flex flex-col">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-xl font-bold text-gray-800">{isViewMode ? 'View' : isEditMode ? 'Edit' : 'Create'} Designation</h2>
            <button onClick={handleClose} className="text-gray-500 hover:text-gray-700"><FaTimes className="text-xl" /></button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Designation Title *</label>
              <input
                type="text" name="title" value={formData.title} onChange={handleInputChange}
                disabled={isViewMode} className={`w-full px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-red-500 ${formErrors.title ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="e.g. Senior Software Engineer"
              />
              {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
                <input
                    type="number"
                    name="level"
                    value={formData.level}
                    onChange={handleInputChange}
                    // Scroll the input field out of view
                    onWheel={(e) => e.target.blur()} 
                    disabled={isViewMode}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation ID</label>
                <input
                  type="text" value={initialData?.designationId || 'Auto-generated'}
                  disabled className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed outline-none "
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SelectDropdown 
                    label="Department" 
                    options={[{ name: 'Select Department', _id: '' }, ...departments]} 
                    value={formData.departmentId} 
                    onChange={(val) => setFormData(p => ({...p, departmentId: val}))} 
                    valueKey="_id" labelKey="name" 
                    disabled={isViewMode}
                    zIndex={66} // Ensure dropdown appears above overlay when not in view mode

                />
                <SelectDropdown 
                    label="Division" 
                    options={[{ name: 'Select Division', _id: '' }, ...divisions]} 
                    value={formData.divisionId} 
                    onChange={(val) => setFormData(p => ({...p, divisionId: val}))} 
                    valueKey="_id" labelKey="name" 
                    disabled={isViewMode}
                    zIndex={66} // Ensure dropdown appears above overlay when not in view mode
                />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description" value={formData.description} onChange={handleInputChange}
                disabled={isViewMode} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none  focus:ring-2 focus:ring-red-500"
                placeholder="Enter responsibilities..."
              />
            </div>

            {!isViewMode && (
              <div className="flex gap-3 pt-6">
                <button type="button" onClick={handleClose} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                  {loading ? 'Processing...' : isEditMode ? 'Update' : 'Create'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default DesignationForm;